<template>
  <div class="popular-list-container" v-if="plate_code" @scroll="handleScroll">
    <div v-if="loading && stocks.length === 0" class="text-center py-3">
      <b-spinner small variant="primary"></b-spinner>
    </div>
    <div v-else-if="stocks.length === 0" class="text-center py-3 text-muted small" style="font-size: 11px;">
      暂无数据
    </div>
    <div v-else class="popular-items-list">
      <div 
        v-for="(item, index) in filteredStocks" 
        :key="item.stock_code" 
        class="popular-item-row"
        :class="getRowHighlightClass(item.stock_name)"
        :data-stock-name="item.stock_name"
        :data-stock-code="item.stock_code"
        v-b-toggle.sidebar-right-stock
        @click="handleRowClick(item)"
      >
        <!-- 序号 -->
        <div class="rank-badge" :class="getRankClass(index + 1)">
          {{ index + 1 }}
        </div>
        
        <!-- 股票名称 -->
        <div class="stock-name" :title="item.stock_name">
          {{ item.stock_name }}
        </div>
        
        <!-- 涨幅 -->
        <div class="stock-pct" :style="getPctStyle(item.px_change_rate)">
          {{ formatPct(item.px_change_rate) }}
        </div>
      </div>
      
      <!-- 加载更多 spinner -->
      <div v-if="loading && stocks.length > 0" class="text-center py-2">
        <b-spinner small variant="secondary"></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from "network/request.js"

export default {
  name: "StockListOnPlate",
  props: {
    plate_code: {
      type: String,
      default: ""
    },
    plate_type: {
      type: Number,
      default: 17
    },
    date1: {
      type: String,
      default: ""
    },
    highlightStockName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      stocks: [],
      page: 1,
      limit: 30,
      total: 0,
      changeTimer: null
    };
  },
  computed: {
    filteredStocks() {
      return this.stocks || [];
    }
  },
  watch: {
    plate_code: {
      immediate: true,
      handler() {
        this.onPropsChange();
      }
    },
    plate_type: {
      immediate: true,
      handler() {
        this.onPropsChange();
      }
    },
    date1: {
      immediate: true,
      handler() {
        this.onPropsChange();
      }
    }
  },
  mounted() {
    this.$root.$on('stock-sidebar-stock-changed', this.onStockSidebarStockChanged);
  },
  beforeDestroy() {
    if (this.changeTimer) {
      clearTimeout(this.changeTimer);
    }
    this.$root.$off('stock-sidebar-stock-changed', this.onStockSidebarStockChanged);
  },
  methods: {
    onPropsChange() {
      if (this.changeTimer) clearTimeout(this.changeTimer);
      this.changeTimer = setTimeout(() => {
        this.fetchStockRates(true);
      }, 50);
    },
    fetchStockRates(reset = false) {
      if (!this.plate_code || !this.date1) return;
      
      if (reset) {
        this.page = 1;
        this.total = 0;
      }
      
      this.loading = true;
      const params = {
        date1: this.date1,
        page: this.page,
        limit: this.limit
      };

      request_zzquant({
        url: `/v3/market/plates/${this.plate_type}/${this.plate_code}/stocks/rates`,
        params: params
      })
      .then(res => {
        const resData = res.data.data || {};
        const list = resData.list || [];
        this.total = resData.total || 0;
        
        if (reset) {
          this.stocks = list;
        } else {
          // Append data, filter duplicates by stock_code
          const currentCodes = new Set(this.stocks.map(s => s.stock_code));
          const newItems = list.filter(item => !currentCodes.has(item.stock_code));
          this.stocks = [...this.stocks, ...newItems];
        }
        this.checkAndLoadMore();
      })
      .catch(err => {
        console.error("Fetch stock rates error:", err);
        if (reset) this.stocks = [];
      })
      .finally(() => {
        this.loading = false;
      });
    },
    checkAndLoadMore() {
      this.$nextTick(() => {
        const container = this.$el;
        if (container && container.scrollHeight > 0 && (container.scrollHeight - container.clientHeight <= 10)) {
          if (!this.loading && this.stocks.length < this.total) {
            this.page += 1;
            this.fetchStockRates(false);
          }
        }
      });
    },
    handleScroll(e) {
      const container = e.target;
      // Scroll bottom trigger (within 50px of bottom)
      if (container.scrollHeight - Math.ceil(container.scrollTop) - container.clientHeight <= 50) {
        if (!this.loading && this.stocks.length < this.total) {
          this.page += 1;
          this.fetchStockRates(false);
        }
      }
    },
    getRankClass(rank) {
      if (rank === 1) return 'rank-1';
      if (rank === 2) return 'rank-2';
      if (rank === 3) return 'rank-3';
      return 'rank-other';
    },
    getPctStyle(val) {
      const v = parseFloat(val);
      if (isNaN(v) || v === 0) return { 'color': 'var(--text-muted, #adbbca)' };
      const isDark = document.body.classList.contains('theme-dark');
      const isUp = v > 0;
      let color;
      if (isUp) {
        color = isDark ? '#ff6b6b' : '#dc3545';
      } else {
        color = isDark ? '#52c41a' : '#28a745';
      }
      return {
        'color': color,
        'font-weight': '600'
      };
    },
    formatPct(val) {
      if (val === undefined || val === null) return "-";
      return (val > 0 ? "+" : "") + val.toFixed(2) + "%";
    },
    handleRowClick(item) {
      this.$emit("stock-clicked", item.stock_name);
      const stockList = (this.filteredStocks || []).map(s => ({
        stock_code: s.stock_code,
        stock_name: s.stock_name,
        date1: this.date1
      }));
      const payload = {
        stock_code: item.stock_code,
        stock_name: item.stock_name,
        date1: this.date1,
        stock_list: stockList
      };
      this.$emit("show_sidebar", payload);
      this.$root.$emit('stock-sidebar-set-data', payload);
    },
    onStockSidebarStockChanged(payload) {
      if (!payload || !payload.stock_code) return;
      const foundIdx = (this.stocks || []).findIndex(s => s.stock_code === payload.stock_code);
      if (foundIdx >= 0) {
        this.$nextTick(() => {
          const el = this.$el.querySelector(`[data-stock-code="${payload.stock_code}"]`);
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      }
    },
    getRowHighlightClass(stockName) {
      if (this.highlightStockName && this.highlightStockName === stockName) {
        return 'highlight-clicked';
      }
      return '';
    }
  }
};
</script>

<style scoped>
.popular-list-container {
  display: flex;
  flex-direction: column;
  background-color: var(--bg-card, #ffffff);
  border: 1px solid var(--border-color, #e9ecef);
  border-radius: 4px;
  padding: 2px;
  max-height: 700px;
  overflow-y: auto;
}

/* 美化滚动条 */
.popular-list-container::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.popular-list-container::-webkit-scrollbar-track {
  background: transparent;
}

.popular-list-container::-webkit-scrollbar-thumb {
  background-color: rgba(144, 147, 153, 0.3);
  border-radius: 3px;
  transition: background-color 0.3s;
}

.popular-list-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(144, 147, 153, 0.5);
}

.popular-items-list {
  display: flex;
  flex-direction: column;
}

.popular-item-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 3px 2px;
  cursor: pointer;
  font-size: 11px;
  border-bottom: 1px solid var(--border-color, #f8f9fa);
  transition: background-color 0.15s;
  white-space: nowrap;
  overflow: hidden;
}

.popular-item-row:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.rank-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-size: 9px;
  font-weight: bold;
  border-radius: 2px;
  flex-shrink: 0;
}

.rank-1 {
  background-color: #dc3545;
  color: #fff;
}

.rank-2 {
  background-color: #fd7e14;
  color: #fff;
}

.rank-3 {
  background-color: #ffc107;
  color: #212529;
}

.rank-other {
  background-color: var(--bg-muted, #e9ecef);
  color: var(--text-muted, #6c757d);
}

.stock-name {
  width: 55px;
  flex-shrink: 0;
  text-align: left;
  margin-left: 6px;
  font-weight: 500;
  color: var(--text-main, #212529);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-pct {
  width: 50px;
  text-align: left;
  margin-left: 10px;
  font-size: 10px;
  flex-shrink: 0;
  white-space: nowrap;
}

/* Dark theme overrides */
body.theme-dark .popular-item-row {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

body.theme-dark .rank-other {
  background-color: #343a40;
  color: #adbbca;
}

body.theme-dark .stock-name {
  color: #ced4da;
}

.highlight-clicked {
  background-color: rgba(111, 66, 193, 0.15) !important;
  box-shadow: inset 0 0 0 1.5px rgba(111, 66, 193, 0.6);
  border-radius: 2px;
}

body.theme-dark .highlight-clicked {
  background-color: rgba(157, 107, 230, 0.2) !important;
  box-shadow: inset 0 0 0 1.5px rgba(157, 107, 230, 0.7);
}

@media (max-width: 768px) {
  .popular-item-row {
    font-size: 10px;
    padding: 3px 1px;
  }
  .rank-badge {
    width: 12px;
    height: 12px;
    font-size: 8px;
  }
  .stock-pct {
    width: 45px;
    font-size: 9px;
    margin-left: 5px;
  }
  .stock-name {
    width: 50px;
    margin-left: 3px;
  }
}
</style>
